import { useSyncExternalStore } from "react";

import {
  BASE_UI_CONFIG,
  getResolvedUiConfig,
  subscribeToUiConfigOverlay,
  updateUiConfigOverlay,
} from "~/lib/ui-config";
import {
  preserveUiConfigNumberFormatOverlayFields,
} from "~/lib/ui-config/number-format-config";
import {
  getActiveLocale,
  LANGUAGE_REGISTRY,
  subscribeToActiveLocale,
} from "./language-lens";

export interface NumberFormatModeRegistryEntry {
  readonly formattingLocale: string | null;
}

function defineNumberFormatModeRegistry<const Mode extends string>(
  registry: Readonly<Record<Mode, NumberFormatModeRegistryEntry>>,
): Readonly<Record<Mode, NumberFormatModeRegistryEntry>> {
  return registry;
}

export const NUMBER_FORMAT_MODE_REGISTRY = defineNumberFormatModeRegistry({
  auto: { formattingLocale: null },
  "dot-decimal": { formattingLocale: "en-US" },
  "comma-decimal": { formattingLocale: "de-DE" },
  "space-grouped-comma-decimal": { formattingLocale: "ru-RU" },
});

export type NumberFormatMode = keyof typeof NUMBER_FORMAT_MODE_REGISTRY;

type NumberFormatLensListener = () => void;
const NUMBER_FORMAT_EXAMPLE_FORMATTERS = new Map<string, Intl.NumberFormat>();

export function isNumberFormatMode(mode: string): mode is NumberFormatMode {
  return Object.hasOwn(NUMBER_FORMAT_MODE_REGISTRY, mode);
}

export function getNumberFormatMode(): NumberFormatMode {
  const mode = getResolvedUiConfig().lenses.numberFormat.mode;
  return isNumberFormatMode(mode) ? mode : "auto";
}

export function setNumberFormatMode(mode: NumberFormatMode): void {
  if (getNumberFormatMode() === mode) {
    return;
  }

  updateUiConfigOverlay((overlay) => ({
    ...overlay,
    lenses: {
      ...overlay.lenses,
      numberFormat: {
        ...preserveUiConfigNumberFormatOverlayFields(
          overlay.lenses?.numberFormat,
        ),
        mode,
      },
    },
  }));
}

export function subscribeToNumberFormatLens(
  listener: NumberFormatLensListener,
): () => void {
  return subscribeToUiConfigOverlay(listener);
}

function getLanguageFormattingLocale(): string {
  return LANGUAGE_REGISTRY[getActiveLocale()].formattingLocale;
}

export function getNumberFormattingLocaleForMode(
  mode: NumberFormatMode,
): string {
  return (
    NUMBER_FORMAT_MODE_REGISTRY[mode].formattingLocale ??
    getLanguageFormattingLocale()
  );
}

export function formatNumberFormatModeExample(mode: NumberFormatMode): string {
  const locale = getNumberFormattingLocaleForMode(mode);
  let formatter = NUMBER_FORMAT_EXAMPLE_FORMATTERS.get(locale);
  if (formatter === undefined) {
    formatter = new Intl.NumberFormat(locale, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
    NUMBER_FORMAT_EXAMPLE_FORMATTERS.set(locale, formatter);
  }

  return formatter.format(1234.56);
}

export function getNumberFormattingLocale(): string {
  return getNumberFormattingLocaleForMode(getNumberFormatMode());
}

export function useNumberFormatLens(): {
  mode: NumberFormatMode;
  setMode: typeof setNumberFormatMode;
} {
  const mode = useSyncExternalStore(
    subscribeToNumberFormatLens,
    getNumberFormatMode,
    () => BASE_UI_CONFIG.lenses.numberFormat.mode,
  );

  return { mode, setMode: setNumberFormatMode };
}

export function useNumberFormattingLocale(): string {
  return useSyncExternalStore(
    subscribeToNumberFormattingLocale,
    getNumberFormattingLocale,
    () =>
      NUMBER_FORMAT_MODE_REGISTRY[BASE_UI_CONFIG.lenses.numberFormat.mode]
        .formattingLocale ??
      LANGUAGE_REGISTRY[BASE_UI_CONFIG.lenses.language.locale].formattingLocale,
  );
}

function subscribeToNumberFormattingLocale(
  listener: NumberFormatLensListener,
): () => void {
  const unsubscribeFromNumberFormatLens = subscribeToNumberFormatLens(listener);
  const unsubscribeFromActiveLocale = subscribeToActiveLocale(listener);

  return () => {
    unsubscribeFromNumberFormatLens();
    unsubscribeFromActiveLocale();
  };
}
